import { createFileRoute, Link } from "@tanstack/react-router";
import portraitImg from "@/assets/dj-mariage-portrait.jpg";
import { useReveal } from "@/hooks/use-reveal";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
const URL = "https://dj-val.fr/guide-mariage-vaucluse";
const PUBLISHED = "2026-06-10";
const MODIFIED = "2026-06-10";

const TOC = [
  { id: "lieux", t: "1. Choisir son lieu : Luberon, Alpilles ou Avignon ?" },
  { id: "budget", t: "2. Budget moyen d'un mariage dans le Vaucluse" },
  { id: "planning", t: "3. Le rétroplanning J-12 mois → Jour J" },
  { id: "ceremonie", t: "4. Cérémonie laïque & sonorisation" },
  { id: "cocktail", t: "5. Cocktail : musique et ambiance" },
  { id: "ouverture", t: "6. Ouverture de bal : fumée, étincelles, magie" },
  { id: "playlist", t: "7. Construire une playlist multi-générations" },
  { id: "technique", t: "8. Check-list technique pour la salle" },
  { id: "faq", t: "FAQ — questions fréquentes" },
];

const FAQ = [
  {
    q: "Quel budget pour un mariage dans le Vaucluse en 2026 ?",
    a: "Pour 100 invités, comptez en moyenne 25 000 € à 38 000 € tout compris. Le lieu de réception représente 30 à 40 % du budget (5 000–12 000 €), le traiteur 25 à 35 % (60–110 €/invité), l'animation DJ 4 à 6 % (1 200–2 200 €), la décoration et fleurs 8 à 12 %, le photographe 6 à 10 %. Un mariage en haute saison (juin/septembre) coûte 15 à 20 % de plus qu'en avril ou octobre.",
  },
  {
    q: "Quand réserver son DJ pour un mariage dans le Luberon ?",
    a: "Au moins 9 à 14 mois à l'avance pour les samedis de mai à septembre. Les bons DJ professionnels de la région sont en moyenne réservés 11 mois avant la date. Pour un mariage hors saison ou en semaine, 4 à 6 mois suffisent généralement.",
  },
  {
    q: "Faut-il une autorisation pour les feux d'artifice ou les étincelles froides ?",
    a: "Les étincelles froides (cold sparks) intérieures ne nécessitent aucune autorisation — elles sont non pyrotechniques, sans chaleur ni fumée. Les vrais feux d'artifice nécessitent en revanche une déclaration en préfecture et un artificier diplômé. Demandez à votre lieu : la majorité des domaines du Luberon interdit les feux extérieurs entre juin et septembre (risque incendie).",
  },
  {
    q: "Quelle puissance sono pour 100, 150 ou 200 invités ?",
    a: "Pour 100 invités en intérieur, un système RCF HDL 6-A ou Electro-Voice EKX-15P + caisson de basses suffit largement (1 500 W). Pour 150 invités en extérieur, ajoutez un second caisson. Au-delà de 200 invités ou pour les très grandes salles voûtées, comptez sur un line-array (HDL 20-A ou équivalent).",
  },
  {
    q: "Le DJ peut-il sonoriser une cérémonie laïque en extérieur ?",
    a: "Oui — c'est même fortement recommandé. Un système autonome sur batterie (Bose S1 Pro ou Electro-Voice Everse 8), deux micros HF Shure et un micro main suffisent à couvrir une cérémonie de 80 à 150 invités en plein air. Prévoyez un repli sonore si la cérémonie a lieu sous des oliviers (l'absorption acoustique des feuillages est importante).",
  },
];

const VENUES = [
  { region: "Luberon", lieux: "Bonnieux, Ménerbes, Lourmarin, Gordes, Roussillon — bastides en pierre, domaines viticoles, mas restaurés. Ambiance authentique, vue imprenable, capacité 80–200 invités." },
  { region: "Alpilles", lieux: "Saint-Rémy-de-Provence, Maussane, Eygalières, Les Baux — mas provençaux, cyprès, oliveraies. Mariages élégants, souvent en extérieur, capacité 100–250." },
  { region: "Avignon & Comtat", lieux: "Châteaux historiques, hôtels particuliers du centre, domaines près des remparts. Idéal pour les mariages multi-jours avec visite du Palais des Papes." },
  { region: "Camargue & sud", lieux: "Manades, mas isolés entre Arles et les Saintes-Maries — pour les mariages atypiques, gardians et taureaux possibles." },
];

const BUDGET = [
  { poste: "Lieu de réception", part: "30–40 %", min: "5 000 €", max: "12 000 €" },
  { poste: "Traiteur (60–110 €/invité)", part: "25–35 %", min: "6 000 €", max: "11 000 €" },
  { poste: "Photographe & vidéaste", part: "6–10 %", min: "1 800 €", max: "3 500 €" },
  { poste: "DJ & animation", part: "4–6 %", min: "1 200 €", max: "2 200 €" },
  { poste: "Décoration & fleurs", part: "8–12 %", min: "2 000 €", max: "4 500 €" },
  { poste: "Tenues & beauté", part: "5–8 %", min: "1 500 €", max: "3 000 €" },
  { poste: "Wedding planner (optionnel)", part: "+8–15 %", min: "2 500 €", max: "6 000 €" },
];

const TIMELINE = [
  { d: "J-12 mois", t: "Choix du lieu, signature du contrat, premier acompte (généralement 30 %)" },
  { d: "J-10 mois", t: "Réservation DJ, photographe, traiteur. Ces 3 prestataires sont les plus pris d'assaut dans le Vaucluse." },
  { d: "J-9 mois", t: "Choix du thème, début de la déco, save-the-date." },
  { d: "J-6 mois", t: "Essayages tenues, dégustation traiteur, choix des fleurs." },
  { d: "J-4 mois", t: "Envoi des faire-part, brief musical au DJ (must-play, no-go), réunion avec le maître de cérémonie." },
  { d: "J-2 mois", t: "Plan de table, réunion technique au lieu (acoustique, électricité, accès), commande du gâteau." },
  { d: "J-1 mois", t: "Confirmation du nombre exact d'invités au traiteur, finalisation du déroulé minute par minute avec le DJ." },
  { d: "J-7 jours", t: "Dernier appel à tous les prestataires, vérification des horaires d'arrivée et de démontage." },
  { d: "Jour J", t: "Profiter. Tout a été préparé." },
];

export const Route = createFileRoute("/guide-mariage-vaucluse")({
  head: () => ({
    meta: [
      { title: "Guide du mariage dans le Vaucluse 2026 | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("Organiser son mariage dans le Vaucluse en 2026 : choisir son lieu, budget par poste, rétroplanning, cérémonie laïque, ouverture de bal et playlist.")
      },
      { property: "og:title", content: "Guide du Mariage dans le Vaucluse 2026 — DJ Val" },
      { property: "og:description", content: metaDesc("Tout pour organiser votre mariage en Provence : lieux, budget, planning, musique.") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { property: "og:image", content: `https://dj-val.fr${portraitImg}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://dj-val.fr${portraitImg}` },
      { property: "article:published_time", content: PUBLISHED },
      { property: "article:modified_time", content: MODIFIED },
      { property: "article:author", content: "DJ Val" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Guide du Mariage dans le Vaucluse 2026 — Lieux, Budget, Musique",
          image: portraitImg,
          datePublished: PUBLISHED,
          dateModified: MODIFIED,
          author: { "@type": "Person", "@id": "https://dj-val.fr/#val", name: "DJ Val" },
          publisher: { "@id": "https://dj-val.fr/#business" },
          mainEntityOfPage: URL,
          inLanguage: "fr-FR",
          articleSection: "Guides mariage",
          about: ["Mariage dans le Vaucluse", "Mariage Luberon", "Mariage Provence", "DJ Mariage"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://dj-val.fr/" },
            { "@type": "ListItem", position: 2, name: "Guide mariage Vaucluse", item: URL },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  useReveal();
  return (
    <div className="bg-background text-muted-foreground">
      {/* HERO */}
      <section className="relative pt-32 pb-16 px-6 overflow-hidden border-b border-foreground/10">
        <div className="max-w-3xl mx-auto text-center sr" data-reveal>
          <p className="eyebrow text-foreground">Guide complet · 15 min de lecture</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-foreground mt-6 leading-tight">
            Organiser son <span className="italic gold-text-shine">mariage</span>
            <span className="block">dans le Vaucluse</span>
          </h1>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <p className="text-base text-foreground/90 font-light leading-relaxed">
            Lieux, budget, planning, cérémonie, musique : tout ce que nous avons appris en 20 ans de mariages
            animés en Provence — Luberon, Alpilles et Avignon. Mis à jour pour 2026.
          </p>
        </div>
      </section>
      <ReviewsProof />

      {/* TL;DR */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto bg-card border-l-2 border-[var(--gold)] p-6 sr" data-reveal>
          <p className="eyebrow text-foreground">En résumé</p>
          <ul className="mt-4 space-y-2 text-sm font-light leading-relaxed">
            <li><strong className="text-foreground">Budget moyen 2026 :</strong> 25 000–38 000 € pour 100 invités, tout compris.</li>
            <li><strong className="text-foreground">Réserver son DJ :</strong> 9 à 14 mois avant la date, plus tôt pour juin et septembre.</li>
            <li><strong className="text-foreground">Lieux phares :</strong> Bonnieux, Gordes, Ménerbes (Luberon), Saint-Rémy (Alpilles), Avignon centre.</li>
            <li><strong className="text-foreground">Cérémonie laïque :</strong> 100 % faisable en plein air avec une sono autonome sur batterie.</li>
            <li><strong className="text-foreground">Ouverture de bal :</strong> étincelles froides intérieures (sans autorisation) + fumée lourde = effet « waouh » sans risque.</li>
          </ul>
        </div>
      </section>

      {/* TOC */}
      <section className="py-8 px-6 border-y border-foreground/10 bg-card">
        <div className="max-w-3xl mx-auto sr" data-reveal>
          <p className="eyebrow text-foreground mb-4">Sommaire</p>
          <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            {TOC.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-foreground/90 hover:text-[var(--gold)] transition-colors">
                  {s.t}
                </a>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CONTENT */}
      <article className="py-20 px-6">
        <div className="max-w-3xl mx-auto space-y-16 text-base font-light leading-relaxed">

          {/* 1 */}
          <section id="lieux" className="sr" data-reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">1. Choisir son lieu : Luberon, Alpilles ou Avignon ?</h2>
            <span className="gold-rule !w-12 my-6" />
            <p>
              Le Vaucluse offre trois grandes familles de lieux de réception, chacune avec sa personnalité. Le bon
              choix dépend de votre style, de votre budget et du nombre d'invités.
            </p>
            <div className="mt-6 space-y-5">
              {VENUES.map((v) => (
                <div key={v.region} className="border-l border-[var(--gold)]/40 pl-5">
                  <h3 className="font-serif-display text-xl text-foreground">{v.region}</h3>
                  <p className="text-sm mt-2">{v.lieux}</p>
                </div>
              ))}
            </div>
            <p className="mt-6">
              <strong className="text-foreground">Notre conseil :</strong> visitez au moins 3 lieux différents,
              espacés de 1 à 2 semaines. La première visite biaise toujours le jugement — on a tendance à s'enthousiasmer
              ou à hésiter sans recul. Visitez idéalement à la saison de votre mariage : un mas du Luberon en juillet
              n'a rien à voir avec le même mas en mars.
            </p>
          </section>

          {/* 2 */}
          <section id="budget" className="sr" data-reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">2. Budget moyen d'un mariage dans le Vaucluse</h2>
            <span className="gold-rule !w-12 my-6" />
            <p>
              Pour un mariage de 100 invités en 2026, le budget moyen constaté dans le Vaucluse se situe entre
              <strong className="text-foreground"> 25 000 € et 38 000 €</strong>. Voici la répartition typique :
            </p>
            <div className="mt-6 overflow-hidden border border-foreground/10">
              <table className="w-full text-sm">
                <thead className="bg-card text-foreground">
                  <tr>
                    <th className="text-left p-3 font-serif-display font-normal">Poste</th>
                    <th className="text-left p-3 font-serif-display font-normal">% du budget</th>
                    <th className="text-left p-3 font-serif-display font-normal">Fourchette</th>
                  </tr>
                </thead>
                <tbody>
                  {BUDGET.map((b) => (
                    <tr key={b.poste} className="border-t border-foreground/10">
                      <td className="p-3 text-foreground/85">{b.poste}</td>
                      <td className="p-3 text-foreground/85">{b.part}</td>
                      <td className="p-3 text-foreground/85">{b.min} – {b.max}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-6">
              <strong className="text-foreground">Économie cachée :</strong> un mariage en avril, octobre ou
              novembre coûte 15 à 20 % moins cher qu'en juin/septembre — pour la même qualité de prestation et un
              climat souvent superbe en Provence.
            </p>
          </section>

          {/* 3 */}
          <section id="planning" className="sr" data-reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">3. Le rétroplanning J-12 mois → Jour J</h2>
            <span className="gold-rule !w-12 my-6" />
            <p>
              L'erreur la plus fréquente : penser qu'on a le temps. À 6 mois de la date, les meilleurs prestataires
              du Vaucluse sont déjà pris. Voici le rétroplanning optimal :
            </p>
            <div className="mt-6 space-y-3">
              {TIMELINE.map((t) => (
                <div key={t.d} className="grid grid-cols-[100px_1fr] gap-4 py-3 border-b border-foreground/10 last:border-0">
                  <p className="font-serif-display italic text-[var(--gold)] text-sm">{t.d}</p>
                  <p className="text-sm">{t.t}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 4 */}
          <section id="ceremonie" className="sr" data-reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">4. Cérémonie laïque & sonorisation</h2>
            <span className="gold-rule !w-12 my-6" />
            <p>
              La cérémonie laïque est devenue le moment le plus émotionnel du mariage. Mais c'est aussi le moment
              techniquement le plus délicat : extérieur, vent, dispersion sonore, micros pour les officiants et les
              témoins.
            </p>
            <p className="mt-4">
              <strong className="text-foreground">Ce qu'il faut prévoir :</strong> deux micros HF (Shure ULXD ou
              Sennheiser EW-D) — un main pour l'officiant, un lavalier de secours. Une sono autonome sur batterie
              (Bose S1 Pro ou Electro-Voice Everse) si la cérémonie est loin du dîner. Une console pour gérer en
              direct les bandes-son (entrée des mariés, sortie, intermèdes).
            </p>
            <p className="mt-4">
              <strong className="text-foreground">À éviter :</strong> les petites enceintes Bluetooth grand public.
              Elles ne couvrent pas un public de plus de 30 personnes en extérieur, et les micros chinois bon marché
              décrochent en plein discours. Mieux vaut louer ou inclure la cérémonie dans votre prestation DJ.
            </p>
          </section>

          {/* 5 */}
          <section id="cocktail" className="sr" data-reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">5. Cocktail : musique et ambiance</h2>
            <span className="gold-rule !w-12 my-6" />
            <p>
              Le cocktail dure en moyenne 2 h dans le Vaucluse. C'est un moment d'échange, pas de danse. La musique doit
              être présente mais discrète : volume modéré, sélections jazzy, bossa, lounge, deep house calme. C'est
              aussi le moment où les invités rencontrent le DJ — bonjour, micro pour l'animation des jeux, présentation
              du déroulé de soirée.
            </p>
            <p className="mt-4">
              <strong className="text-foreground">Idée appréciée :</strong> un photobooth installé pendant le
              cocktail, qui tourne en autonomie jusqu'à la fin de soirée. Voir notre{" "}
              <Link to="/location-materiel/$slug" params={{ slug: "photobooth" }} className="text-[var(--gold)] hover:underline">location photobooth premium</Link>.
            </p>
          </section>

          {/* 6 */}
          <section id="ouverture" className="sr" data-reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">6. Ouverture de bal : fumée, étincelles, magie</h2>
            <span className="gold-rule !w-12 my-6" />
            <p>
              L'ouverture de bal est LE moment Instagram du mariage. En 2026, trois effets reviennent en boucle :
            </p>
            <ul className="mt-4 space-y-3 list-disc list-inside marker:text-[var(--gold)]">
              <li>
                <strong className="text-foreground">Fumée lourde (low fog) :</strong> nappe de fumée qui glisse au
                ras du sol pendant les premières secondes de la chanson. Effet « danse dans les nuages ». Nécessite
                glace carbonique + machine dédiée.
              </li>
              <li>
                <strong className="text-foreground">Étincelles froides (cold sparks) :</strong> jets verticaux ou
                cascades sur scène. Aucune chaleur, sans flamme, autorisés en intérieur sans déclaration. Effet
                feu d'artifice 100 % safe.
              </li>
              <li>
                <strong className="text-foreground">Canon à confettis ou CO₂ :</strong> jet ponctuel sur le drop de
                la chanson. Très visuel, demande un nettoyage du sol après.
              </li>
            </ul>
            <p className="mt-4">
              <strong className="text-foreground">À vérifier avec votre lieu :</strong> tous les domaines n'acceptent
              pas la fumée lourde (détecteurs incendie incompatibles). Demandez systématiquement avant de réserver
              l'effet.
            </p>
          </section>

          {/* 7 */}
          <section id="playlist" className="sr" data-reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">7. Construire une playlist multi-générations</h2>
            <span className="gold-rule !w-12 my-6" />
            <p>
              Un bon DJ ne joue pas SA playlist : il joue la vôtre, augmentée de sa lecture en temps réel de la piste.
              Voici la méthode que nous appliquons depuis 20 ans :
            </p>
            <ul className="mt-4 space-y-3 list-disc list-inside marker:text-[var(--gold)]">
              <li><strong className="text-foreground">Must-play :</strong> 15 à 20 morceaux qui DOIVENT passer (ouverture, premiers slows, morceaux familiaux).</li>
              <li><strong className="text-foreground">No-go :</strong> 5 à 10 morceaux ou artistes interdits (souvent un ex, ou un morceau associé à un mauvais souvenir).</li>
              <li><strong className="text-foreground">Univers de prédilection :</strong> rock 70s, funk 80s, FR 2000, électro 2010, latino, R&B, etc. — pour chaque tranche d'âge présente.</li>
              <li><strong className="text-foreground">Carte blanche au DJ :</strong> sur 30 % de la soirée, pour s'adapter à l'énergie réelle.</li>
            </ul>
            <p className="mt-4">
              <strong className="text-foreground">Le piège classique :</strong> une playlist Spotify de 6 h envoyée
              au DJ avec interdiction d'en sortir. Résultat : la piste se vide vers 1 h du matin parce que les morceaux
              choisis ne « répondent pas » à l'énergie de l'instant. Faites confiance au pro : il lit la piste.
            </p>
          </section>

          {/* 8 */}
          <section id="technique" className="sr" data-reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">8. Check-list technique pour la salle</h2>
            <span className="gold-rule !w-12 my-6" />
            <p>Avant de signer avec un lieu, vérifiez ces points :</p>
            <ul className="mt-4 space-y-2 list-disc list-inside marker:text-[var(--gold)]">
              <li>Puissance électrique disponible (au minimum 32 A monophasé pour une grande soirée)</li>
              <li>Limite sonore (en dB) imposée par le propriétaire ou la mairie</li>
              <li>Heure de fin imposée (1 h ? 3 h ? sans limite ?)</li>
              <li>Voisinage proche (un voisin à 50 m peut tout faire arrêter à 23 h)</li>
              <li>Accès véhicule au plus près de la salle (pour décharger 200 kg de matos)</li>
              <li>Possibilité d'installer la sono extérieure et intérieure (cocktail + dîner + danse)</li>
              <li>Repli pluie obligatoire et fonctionnel</li>
              <li>Hauteur sous plafond, présence de poutres apparentes (impact sur les lyres et l'acoustique)</li>
              <li>Compatibilité fumée lourde (détecteurs incendie)</li>
            </ul>
            <p className="mt-4">
              Un DJ professionnel local connaît la plupart de ces contraintes par cœur pour les domaines réputés du
              Luberon. N'hésitez pas à demander l'avis de votre DJ dès le choix du lieu — ça peut éviter de mauvaises
              surprises 3 mois avant la date.
            </p>
          </section>

          {/* FAQ */}
          <section id="faq" className="sr" data-reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">FAQ — questions fréquentes</h2>
            <span className="gold-rule !w-12 my-6" />
            <div className="space-y-4">
              {FAQ.map((f, i) => (
                <details key={i} className="border border-foreground/10 p-5 group">
                  <summary className="cursor-pointer font-serif-display text-lg text-foreground">{f.q}</summary>
                  <p className="mt-3 text-sm leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="sr text-center pt-10 border-t border-foreground/10" data-reveal>
            <p className="eyebrow text-foreground">Et concrètement ?</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
              Parlons de <span className="italic text-[var(--gold)]">votre mariage</span>
            </h2>
            <p className="text-sm font-light leading-relaxed mt-4 max-w-xl mx-auto">
              Date, lieu, nombre d'invités, ambiance souhaitée : décrivez-nous votre projet, vous recevrez un
              devis personnalisé sous 24 h.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/contactez-nous" className="bg-[var(--gold)] text-foreground px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">
                Demander un devis
              </Link>
              <Link to="/dj-mariage" className="border border-foreground/20 text-foreground px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
                Voir nos prestations mariage
              </Link>
            </div>
          </section>

        </div>
      </article>
    </div>
  );
}
